import { ChallengeWithUser } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { Button, EmptyState, LoadingSpinner, PatternBackground, UserCard } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { FlatList, RefreshControl, Text, TouchableOpacity, View } from 'react-native';
import { createStyles } from './ChallengesScreen.styles';

export interface ChallengesScreenProps {
  receivedChallenges: ChallengeWithUser[];
  sentChallenges: ChallengeWithUser[];
  loading: boolean;
  loadingMore: boolean;
  activeTab: 'received' | 'sent';
  setActiveTab: (tab: 'received' | 'sent') => void;
  onAccept: (challengeId: string, challengerName?: string) => void;
  onDecline: (challengeId: string) => void;
  onUserPress: (userId: string) => void;
  onTournamentPress: (tournamentId: string) => void;
  onRefresh: () => void;
  onLoadMoreReceived: () => void;
  onLoadMoreSent: () => void;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    error: string;
  };
}

export const ChallengesScreen = ({
  receivedChallenges,
  sentChallenges,
  loading,
  loadingMore,
  activeTab,
  setActiveTab,
  onAccept,
  onDecline,
  onUserPress,
  onTournamentPress,
  onRefresh,
  onLoadMoreReceived,
  onLoadMoreSent,
  colors,
}: ChallengesScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  const renderReceivedChallenge = ({ item }: { item: ChallengeWithUser }) => {
    const isPending = item.status === 'pending';

    return (
      <View style={[styles.challengeCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <UserCard
          userId={item.challengerId}
          userName={item.userName}
          userEmail={item.userEmail}
          userAvatarUrl={item.userAvatarUrl}
          onPress={() => onUserPress(item.challengerId)}
          colors={colors}
        />
        {item.message && (
          <View style={styles.messageContainer}>
            <Ionicons name="chatbubble-outline" size={16} color={colors.textSecondary} />
            <Text style={[styles.message, { color: colors.textSecondary }]}>{item.message}</Text>
          </View>
        )}
        <View style={styles.statusRow}>
          <View style={[styles.statusBadge, { backgroundColor: colors.border }]}>
            <Text style={[styles.statusText, { color: colors.text }]}>{t(`status.challengeStatus.${item.status}`)}</Text>
          </View>
          {isPending && (
            <View style={styles.actions}>
              <Button
                title={t('social.accept')}
                onPress={() => onAccept(item.id, item.userName)}
                variant="primary"
                size="small"
                colors={colors}
              />
              <Button
                title={t('social.decline')}
                onPress={() => onDecline(item.id)}
                variant="secondary"
                size="small"
                colors={colors}
              />
            </View>
          )}
          {item.tournamentId && (
            <TouchableOpacity
              onPress={() => onTournamentPress(item.tournamentId!)}
              style={styles.tournamentLink}
            >
              <Ionicons name="trophy-outline" size={16} color={colors.primary} />
              <Text style={[styles.linkText, { color: colors.primary }]}>{t('social.viewTournament')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderSentChallenge = ({ item }: { item: ChallengeWithUser }) => {
    return (
      <View style={[styles.challengeCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <UserCard
          userId={item.challengedId}
          userName={item.userName}
          userEmail={item.userEmail}
          userAvatarUrl={item.userAvatarUrl}
          onPress={() => onUserPress(item.challengedId)}
          colors={colors}
        />
        {item.message && (
          <View style={styles.messageContainer}>
            <Ionicons name="chatbubble-outline" size={16} color={colors.textSecondary} />
            <Text style={[styles.message, { color: colors.textSecondary }]}>{item.message}</Text>
          </View>
        )}
        <View style={styles.statusRow}>
          <View style={[styles.statusBadge, { backgroundColor: colors.border }]}>
            <Text style={[styles.statusText, { color: colors.text }]}>{t(`status.challengeStatus.${item.status}`)}</Text>
          </View>
          {item.tournamentId && (
            <TouchableOpacity
              onPress={() => onTournamentPress(item.tournamentId!)}
              style={styles.tournamentLink}
            >
              <Ionicons name="trophy-outline" size={16} color={colors.primary} />
              <Text style={[styles.linkText, { color: colors.primary }]}>{t('social.viewTournament')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <PatternBackground>
        <LoadingSpinner />
      </PatternBackground>
    );
  }

  const activeChallenges = activeTab === 'received' ? receivedChallenges : sentChallenges;
  const onLoadMore = activeTab === 'received' ? onLoadMoreReceived : onLoadMoreSent;

  return (
    <PatternBackground>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.tabs}>
          <TouchableOpacity
            style={[
              styles.tab,
              activeTab === 'received' && { borderBottomColor: colors.primary, borderBottomWidth: 2 },
            ]}
            onPress={() => setActiveTab('received')}
          >
            <Text
              style={[
                styles.tabText,
                { color: activeTab === 'received' ? colors.primary : colors.textSecondary },
              ]}
            >
              {t('social.received')} ({receivedChallenges.length})
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.tab,
              activeTab === 'sent' && { borderBottomColor: colors.primary, borderBottomWidth: 2 },
            ]}
            onPress={() => setActiveTab('sent')}
          >
            <Text
              style={[
                styles.tabText,
                { color: activeTab === 'sent' ? colors.primary : colors.textSecondary },
              ]}
            >
              {t('social.sent')} ({sentChallenges.length})
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <FlatList
        data={activeChallenges}
        renderItem={activeTab === 'received' ? renderReceivedChallenge : renderSentChallenge}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.list}
        onEndReached={onLoadMore}
        onEndReachedThreshold={0.5}
        refreshControl={
          <RefreshControl refreshing={loading} onRefresh={onRefresh} tintColor={colors.primary} />
        }
        ListFooterComponent={loadingMore ? <LoadingSpinner /> : null}
        ListEmptyComponent={
          <EmptyState
            icon={<Ionicons name="flash-outline" size={52} color={colors.textTertiary} />}
            title={activeTab === 'received' ? t('social.noChallengesReceived') : t('social.noChallengesSent')}
            subtitle={
              activeTab === 'received'
                ? t('social.whenChallengedAppear')
                : t('social.haventChallengedAnyone')
            }
            colors={colors}
          />
        }
      />
    </PatternBackground>
  );
};
